/** @format */

import {
	CreateQuestionInput,
	UpdateQuestionInput,
	Brand,
	SearchParams,
	SearchResponse,
	Question,
	ParentChildrenResponse,
	QuestionChangeRecord,
} from "@question-bank/shared";
import { authFetch } from "./session";

// API base URL - will be configured per environment
export const getApiBaseUrl = (brand: Brand): string => {
	// For local development, use a mock or proxy
	// In production, this would be the actual API Gateway URL
	const baseUrls: Record<Brand, string> = {
		wjec: import.meta.env.VITE_WJEC_API_URL || "/api/wjec",
		eduqas: import.meta.env.VITE_EDUQAS_API_URL || "/api/eduqas",
	};
	// Strip any trailing slash so callers can safely concatenate `/questions`,
	// `/assets/...`, etc. without producing a double slash in the request URL.
	return baseUrls[brand].replace(/\/+$/, "");
};

/**
 * Build the public URL for a stored asset key (e.g. `assets/<uuid>.png`).
 * Assets are served by the brand's CloudFront asset distribution
 * (decoupled from the API so `<img>` loads never touch API Gateway).
 */
export const getAssetUrl = (brand: Brand, assetKey: string): string => {
	const assetBaseUrls: Record<Brand, string> = {
		wjec: import.meta.env.VITE_WJEC_ASSETS_URL || "",
		eduqas: import.meta.env.VITE_EDUQAS_ASSETS_URL || "",
	};
	return `${assetBaseUrls[brand]}/${assetKey}`;
};

const JSON_HEADERS: HeadersInit = {
	"Content-Type": "application/json",
};

interface ApiErrorBody {
	error?: {
		code?: string;
		message?: string;
		details?: { field?: string; message: string }[];
	};
}

/**
 * Build a readable error from a failed API response, surfacing the
 * structured `{ error: { message, details } }` payload when present
 */
export const buildApiError = async (response: Response, fallback: string): Promise<Error> => {
	const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
	const apiError = body?.error;
	if (apiError?.message) {
		const detailMessages = apiError.details?.map((detail) => detail.message).filter(Boolean) ?? [];
		const suffix = detailMessages.length > 0 ? `: ${detailMessages.join("; ")}` : "";
		return new Error(`${apiError.message}${suffix}`);
	}
	return new Error(`${fallback}: ${response.statusText}`);
};

/**
 * Search questions
 */
export const searchQuestions = async (brand: Brand, params: SearchParams): Promise<SearchResponse> => {
	const baseUrl = getApiBaseUrl(brand);
	const searchParams = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== "") {
			searchParams.append(key, String(value));
		}
	});

	const url = `${baseUrl}/questions?${searchParams.toString()}`;

	const response = await fetch(url, {
		method: "GET",
		headers: JSON_HEADERS,
	});

	if (!response.ok) {
		throw new Error(`Search failed: ${response.statusText}`);
	}

	return response.json();
};

/**
 * Get a single question by ID
 */
export const getQuestion = async (brand: Brand, questionId: string): Promise<Question> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/questions/${questionId}`;

	const response = await fetch(url, {
		method: "GET",
		headers: JSON_HEADERS,
	});

	if (response.status === 404) {
		throw new Error("Question not found");
	}

	if (!response.ok) {
		throw new Error(`Failed to get question: ${response.statusText}`);
	}

	const data = await response.json();
	return data.data;
};

/**
 * Create a new question (requires auth in production)
 */
export const createQuestion = async (
	brand: Brand,
	question: CreateQuestionInput,
): Promise<{ id: string; message: string }> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/questions`;

	const response = await authFetch(url, {
		method: "POST",
		headers: JSON_HEADERS,
		body: JSON.stringify(question),
	});

	if (!response.ok) {
		throw await buildApiError(response, "Failed to create question");
	}

	return response.json();
};

/**
 * Update a question by ID
 */
export const updateQuestion = async (
	questionId: string,
	brand: Brand,
	question: UpdateQuestionInput,
): Promise<{ id: string; message: string }> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/questions/${questionId}`;

	const response = await authFetch(url, {
		method: "PUT",
		headers: JSON_HEADERS,
		body: JSON.stringify(question),
	});

	if (!response.ok) {
		throw await buildApiError(response, "Failed to update question");
	}

	return response.json();
};

/**
 * Delete a question by ID (requires auth)
 */
export const deleteQuestion = async (brand: Brand, questionId: string): Promise<void> => {
	const baseUrl = getApiBaseUrl(brand);
	const response = await authFetch(`${baseUrl}/questions/${questionId}`, {
		method: "DELETE",
		headers: JSON_HEADERS,
	});
	if (!response.ok) {
		const body = (await response.json().catch(() => ({}))) as { message?: string };
		throw new Error(body.message ?? `Failed to delete question: ${response.statusText}`);
	}
};

/**
 * Get every question in a parent ribbon group (lazy-loaded when the
 * admin list expands a parent row).
 */
export const getQuestionChildren = async (brand: Brand, parentQuestionId: string): Promise<ParentChildrenResponse> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/questions/parents/${encodeURIComponent(parentQuestionId)}/children`;

	const response = await fetch(url, {
		method: "GET",
		headers: JSON_HEADERS,
	});

	if (response.status === 404) {
		return { parentQuestionId, items: [] };
	}
	if (!response.ok) {
		throw new Error(`Failed to load children: ${response.statusText}`);
	}

	const body = await response.json();

	return body.data as ParentChildrenResponse;
};

/**
 * Get question change history
 */
export const getQuestionChangeHistory = async (brand: Brand, questionId: string): Promise<QuestionChangeRecord[]> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/questions/${questionId}/change-history`;

	const response = await authFetch(url, {
		method: "GET",
		headers: JSON_HEADERS,
	});

	if (!response.ok) {
		throw await buildApiError(response, "Failed to load change history");
	}

	const data = await response.json();
	return data.data?.history ?? [];
};

/**
 * Current rejection reason for a draft. The public question GET omits it.
 * The change-history route is authenticated and returns the stored value.
 */
export const getRejectionReason = async (brand: Brand, questionId: string): Promise<string | undefined> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/questions/${questionId}/change-history`;

	const response = await authFetch(url, {
		method: "GET",
		headers: JSON_HEADERS,
	});

	if (!response.ok) {
		return undefined;
	}

	const data = await response.json();
	const reason = data.data?.rejectionReason;
	return typeof reason === "string" && reason.trim() ? reason : undefined;
};

/**
 * Check API health
 */
export const checkHealth = async (brand: Brand): Promise<{ status: string }> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/health`;
	const response = await fetch(url, {
		method: "GET",
		headers: JSON_HEADERS,
	});

	if (!response.ok) {
		throw new Error(`Health check failed: ${response.statusText}`);
	}

	return response.json();
};
